import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'

export const post: BlogArticle = {
    slug: 'tesla-lock-sound-too-quiet',
    title: 'Tesla Lock Sound Too Quiet? Here\'s Why + How to Fix It',
    excerpt: 'Tesla lock sound too quiet or barely audible? Check the USB source, file name and path, format and port, then raise the file level with the free converter.',
  image: '/images/blog/generated/tesla-lock-sound-too-quiet.png',
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-05-09',
    category: 'tips-tricks',
    tags: [
      'volume',
      'troubleshooting',
      'lock sound requirements',
      'tesla lock sound volume',
      'quiet',
      'lockchime-wav',
      'install-guide',
      'usb',
    ],
    readingTime: 6,
    featured: false,
    content: `
# Tesla Lock Sound Too Quiet? Here's Why + How to Fix It

You downloaded a custom lock sound, installed it, locked the car, and now you can barely hear it. A quiet custom lock sound usually comes down to one of a few causes: the car is not reading your file, the file is in the wrong format or place, or the file itself is recorded at a low level. This guide goes through them in that order.

## 1. Is the car actually playing your file?

If the car cannot read your \`LockChime.wav\`, it falls back to the factory chime, which can sound quieter than a loud custom sound. Check:

1. Open Toybox → Boombox → Lock Sound and confirm USB is selected. If it fell back to the factory chime, select USB again.
2. The USB drive is formatted as **FAT32 or exFAT**. Windows' default NTFS format is a common cause of a drive the car does not read. On Mac, open Disk Utility and check the drive's format. On Windows, right-click the drive in File Explorer → Properties.
3. The drive is in a USB port that carries data. The glovebox port is the one to use on newer cars; rear ports only charge.

## 2. Is the file in the right place with the right name?

The structure should be:

\`\`\`
USB_ROOT/
  Boombox/
    LockChime.wav
\`\`\`

Common mistakes:
- Folder named **boombox** instead of **Boombox**
- File named **lockchime.wav** or **Lock Chime.wav** instead of **LockChime.wav**
- File placed in a subfolder inside Boombox

If the car does not pick the file up from \`Boombox/\`, try putting \`LockChime.wav\` at the top level of the drive instead. Both locations work.

## 3. Is the file the right format?

Tesla needs a WAV file named \`LockChime.wav\`. We recommend 44.1 kHz, 16-bit, 1–5 seconds and under 1 MB. These are best-practice values for clean playback, not a published Tesla volume spec. Some converters export 48 kHz or 24-bit files; re-export those at 44.1 kHz, 16-bit with the [audio converter](/convert).

## 4. Is the file recorded at a low level?

If the car is playing your file and it is still quiet, the file's own level is the likely cause. Clips are recorded and exported at very different levels, and a clip that sounds fine in headphones can be weak through a small outdoor speaker. Tesla does not publish a loudness target for lock sounds, so we can't give you a decibel figure to aim for. Do this instead:

1. Open the [audio converter](/convert) (it runs in your browser; nothing is uploaded)
2. Upload your sound and trim it to 5 seconds or less
3. Set the loudness preset to **Loud**
4. If you want more, raise the fine-tune gain. It goes up to +6 dB
5. Export, copy the new \`LockChime.wav\` to \`Boombox/\` and test

The converter keeps peaks at or below -1 dBFS, so it will not clip. A very short, peaky sound may not get much louder, because its peak is already near that ceiling. If it is still quiet, try a different sound.

Library sounds are not all at the same level either, so if one downloaded sound is quiet, compare it with another from the [library](/sounds) before assuming the car is at fault.

## 5. Did it get quiet after a software update?

Some owners report quieter lock sounds after updates. Run the checks above first (a software update can leave the car on the factory chime until you select USB again). Then see our [quiet-after-an-update guide](/blog/fix-quiet-tesla-lock-sound-2025-45), which links the owner reports we found and says what we could and could not confirm.

## Frequently Asked Questions

**Why is my Tesla lock sound so quiet?**
The common causes are: (1) the car is not reading your file and is playing the factory chime, (2) the file is in the wrong place, named wrong, or in the wrong format, (3) the USB is in a port that does not carry data, or (4) the file itself is recorded at a low level.

**How do I make my Tesla lock sound louder?**
Re-export the file with the [audio converter](/convert): choose the Loud preset and add up to +6 dB of fine-tune gain if needed, then reinstall.

**What volume should a Tesla lock sound WAV be?**
Tesla does not publish a volume target for custom lock sounds, and we can't give a number we could back up. The recommended format is 44.1 kHz, 16-bit WAV, 1–5 seconds, named \`LockChime.wav\`. Set the level by ear with the converter.

**Why did my Tesla lock sound get quiet after an update?**
Some owners report this, but we have not found a Tesla statement explaining it. Check that USB is still selected under Toybox → Boombox → Lock Sound, then see the [quiet-after-an-update guide](/blog/fix-quiet-tesla-lock-sound-2025-45).

**What WAV format does Tesla require for lock sounds?**
A WAV file named \`LockChime.wav\`, in a \`Boombox\` folder on a FAT32 or exFAT USB drive. We recommend 44.1 kHz, 16-bit, up to 5 seconds. The [audio converter](/convert) exports in that format.

---

Browse ${PUBLIC_SOUND_COUNT_LABEL} lock sounds in the [library](/sounds), each a \`LockChime.wav\` ready for the USB drive, or use the free [audio converter](/convert) to change the level of a sound that comes out too quiet.
`,
  }
